import { useCallback, useEffect, useState } from "react";

/**
 * Per-screen column-visibility state, persisted to localStorage. Stores the set
 * of *hidden* column ids (so the default — nothing stored — shows everything and
 * lets the responsive classes govern). Additive to responsive hiding: a hidden
 * column is always hidden; a visible column still follows its responsive class.
 */
export function useColumnVisibility(storageKey: string) {
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...hidden]));
    } catch {
      // localStorage unavailable (private mode / SSR) — visibility is
      // session-only, which is acceptable.
    }
  }, [hidden, storageKey]);

  const toggle = useCallback((id: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isVisible = useCallback((id: string) => !hidden.has(id), [hidden]);

  return { isVisible, toggle };
}
